import * as DialogPrimitive from '@radix-ui/react-dialog'
import { X } from 'lucide-react'
import * as React from 'react'

import { DialogPortal as FixedDialogPortal } from '@/components/atoms/DialogPortal'
import { CLOSE_ALL_SELECTS_EVENT } from '@/components/atoms/Select'
import { cn } from '@/util/cn'

// Helper to dispatch the close event for all selects
const closeAllSelects = () => {
  window.dispatchEvent(new CustomEvent(CLOSE_ALL_SELECTS_EVENT))
}

const Dialog = DialogPrimitive.Root

const DialogTrigger = DialogPrimitive.Trigger

const DialogPortal = FixedDialogPortal

const DialogClose = DialogPrimitive.Close

const DialogOverlay = ({ ref, className, children, ...props }: React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay> & { ref?: React.RefObject<React.ComponentRef<typeof DialogPrimitive.Overlay>> }) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      'fixed inset-0 z-50 bg-black/80  data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 overflow-auto grid place-items-center',
      className,
    )}
    {...props}
  >
    {children}
  </DialogPrimitive.Overlay>
)
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName

const DialogContent = ({ ref, className, children, ...props }: React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content> & { ref?: React.RefObject<React.ComponentRef<typeof DialogPrimitive.Content>> }) => {
  // Close all selects when dialog starts closing (on animation start)
  const handleAnimationStart = React.useCallback((e: React.AnimationEvent) => {
    // Check if this is the closing animation
    if (e.animationName.includes('exit') || e.animationName.includes('out')) {
      closeAllSelects()
    }
  }, [])

  // Close selects on pointer down on overlay only (not bubbled from children)
  const handleOverlayPointerDown = React.useCallback((e: React.PointerEvent) => {
    // Only close if clicking directly on the overlay, not on children
    if (e.target === e.currentTarget) {
      closeAllSelects()
    }
  }, [])

  return (
    <DialogPortal>
      <DialogOverlay onPointerDown={handleOverlayPointerDown}>
        <DialogPrimitive.Content
          ref={ref}
          className={cn(
            'relative z-50 grid w-full max-w-xl mx-auto gap-4 border border-neutral-200 bg-white p-6 shadow-lg duration-400 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95 data-[state=closed]:zoom-out-95  sm:rounded-lg rounded-lg my-4 mt-24',
            className,
          )}
          onAnimationStart={handleAnimationStart}
          {...props}
        >
          {children}
          <DialogPrimitive.Close className="absolute right-6 top-6 z-10 rounded-sm bg-white opacity-70 transition-opacity hover:opacity-100 focus:outline-none disabled:pointer-events-none data-[state=open]:bg-neutral-100 data-[state=open]:text-neutral-500 dark:bg-neutral-950 dark:data-[state=open]:bg-neutral-800 dark:data-[state=open]:text-neutral-400">
            <X className="size-5" />
            <span className="sr-only">Close</span>
          </DialogPrimitive.Close>
        </DialogPrimitive.Content>
      </DialogOverlay>
    </DialogPortal>
  )
}
DialogContent.displayName = DialogPrimitive.Content.displayName

const DialogHeader = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      'flex flex-col space-y-1.5 text-center sm:text-left',
      className,
    )}
    {...props}
  />
)
DialogHeader.displayName = 'DialogHeader'

const DialogFooter = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      'flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2',
      className,
    )}
    {...props}
  />
)
DialogFooter.displayName = 'DialogFooter'

const DialogTitle = ({ ref, className, ...props }: React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title> & { ref?: React.RefObject<React.ElementRef<typeof DialogPrimitive.Title>> }) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn(
      'text-lg font-semibold leading-none tracking-tight',
      className,
    )}
    {...props}
  />
)
DialogTitle.displayName = DialogPrimitive.Title.displayName

const DialogDescription = ({ ref, className, ...props }: React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description> & { ref?: React.RefObject<React.ElementRef<typeof DialogPrimitive.Description>> }) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn('text-sm text-neutral-500 dark:text-neutral-400', className)}
    {...props}
  />
)
DialogDescription.displayName = DialogPrimitive.Description.displayName

const ScrollableDialogContent = ({ ref, className, children, ...props }: React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content> & { ref?: React.RefObject<React.ComponentRef<typeof DialogPrimitive.Content>> }) => {
  // Close all selects when dialog starts closing
  const handleAnimationStart = React.useCallback((e: React.AnimationEvent) => {
    if (e.animationName.includes('exit') || e.animationName.includes('out')) {
      closeAllSelects()
    }
  }, [])

  // Also close selects on pointer down on the backdrop
  const handleBackdropPointerDown = React.useCallback((e: React.PointerEvent) => {
    // Only if clicking on the backdrop itself, not the dialog content
    if (e.target === e.currentTarget) {
      closeAllSelects()
    }
  }, [])

  return (
    <DialogPortal>
      <DialogOverlay />
      <div 
        className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto p-4"
        onPointerDown={handleBackdropPointerDown}
      >
        <div className="flex min-h-full items-center justify-center py-8">
          <DialogPrimitive.Content
            ref={ref}
            className={cn(
              'relative z-[10000] grid w-full max-w-lg gap-4 border border-neutral-200 bg-white p-6 shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 sm:rounded-lg dark:border-neutral-800 dark:bg-neutral-950 rounded-lg my-4',
              className,
            )}
            onAnimationStart={handleAnimationStart}
            {...props}
          >
            {children}
            <DialogPrimitive.Close className="absolute right-4 top-4 z-10 rounded-sm bg-white opacity-70 transition-opacity hover:opacity-100 focus:outline-none disabled:pointer-events-none data-[state=open]:bg-neutral-100 data-[state=open]:text-neutral-500 dark:bg-neutral-950 dark:data-[state=open]:bg-neutral-800 dark:data-[state=open]:text-neutral-400">
              <X className="size-4" />
              <span className="sr-only">Close</span>
            </DialogPrimitive.Close>
          </DialogPrimitive.Content>
        </div>
      </div>
    </DialogPortal>
  )
}
ScrollableDialogContent.displayName = 'ScrollableDialogContent'

export {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
  DialogTrigger,
  ScrollableDialogContent,
}
